import React, { useState, useEffect, useMemo } from "react";
import { database } from "../../../firebase";
import { ref, get } from "firebase/database";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Box,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
} from "@mui/material";
import { Visibility } from "@mui/icons-material"; // Import the visibility icon
import CustomPagination from "../../Common/Pagination";
import AdminRequestForm from "./SupportFrom/adminRequestForm";
import { X } from "lucide-react";

const Supports = ({ user }) => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState("");
  const [getUser, setUser] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1); // State to manage current page
  const itemsPerPage = 5; // Number of items per page
  const [openPreview, setOpenPreview] = useState(false); // State for modal
  const [previewRequest, setPreviewRequest] = useState(null); // State to store request for preview
  const [officeCounts, setOfficeCounts] = useState({});
  const [open, setOpen] = useState(false);
  // Memoize the offices array
  const offices = useMemo(
    () => [
      "Ingleburn",
      "Auburn",
      "Rouse Hill",
      "Southport",
      "Fitzgibbon",
      "Adelaide",
    ],
    []
  );

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const requestsRef = ref(database, "requests");
        const snapshot = await get(requestsRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          const requestsArray = Object.keys(data).map((key) => ({
            id: key,
            ...data[key],
          }));
          setRequests(requestsArray);
          setLoading(false);
          const counts = offices.reduce((acc, office) => {
            acc[office] = requestsArray.filter(
              (req) => req.office === office
            ).length;
            return acc;
          }, {});
          setOfficeCounts(counts);
        }
      } catch (error) {
        console.error("Error fetching requests:", error);
        setError("Failed to load support requests.");
        setLoading(false);
      }
    };

    fetchRequests();
  }, [offices]);

  useEffect(() => {
    if (selectedOffice) {
      setFilteredRequests(
        requests.filter((request) => request.office === selectedOffice)
      );
    } else {
      setFilteredRequests(requests);
    }
  }, [selectedOffice, requests]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Pagination logic
  const indexOfLastRequest = currentPage * itemsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - itemsPerPage;
  const currentRequests = filteredRequests.slice(
    indexOfFirstRequest,
    indexOfLastRequest
  );

  // Handle preview modal
  const handleOpenPreview = async (request) => {
    setPreviewRequest(request);
    const userRef = ref(database, "profiles/" + request.userId);
    const usersnapshot = await get(userRef);

    if (usersnapshot.exists()) {
      const userData = usersnapshot.val();
      const middleNameSegment = userData.middleName
        ? ` ${userData.middleName}`
        : " ";
      const userName = `${userData.firstName} ${middleNameSegment} ${userData.lastName}`;
      setUser(userName);
    } else {
      setUser("Unknown");
    }

    setOpenPreview(true);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setPreviewRequest(null);
  };

  const MAX_LENGTH = 100; // Adjust as needed

  const [showFullDescription, setShowFullDescription] = useState(false);

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  return (
    <div className="flex flex-col justify-center items-end bg-white p-6">
      {/* Office Count Cards */}
      <div className="flex flex-wrap mb-4 container">
        {offices.map((office) => (
          <div key={office} className="w-1/3 sm:w-1/2 md:w-1/3 lg:w-1/6 p-2">
            <div className="shadow-md border">
              <div className="p-4">
                <Typography variant="h6" className="text-center">
                  {office}
                </Typography>
                <Typography variant="h5" className="text-center font-bold">
                  {officeCounts[office] || 0}
                </Typography>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div>
        {/* Button to Open Modal */}
        <Button
          onClick={() => setOpen(true)}
          className="bg-blue-900 text-white hover:bg-blue-700 hover:text-white"
        >
          New Request
        </Button>

        {open && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 overflow-auto">
            <div className="bg-white rounded-lg shadow-lg max-w-2xl sm:max-w-lg md:max-w-2xl w-full p-6 relative transition-transform overflow-y-auto max-h-[90vh] my-10">
              {/* Close Button - Positioned Correctly */}
              <div className="flex justify-end items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setOpen(false)}
                  className="absolute top-2 right-2"
                >
                  <X className="w-6 h-6" />
                </Button> 
              </div>

              {/* Modal Content */}
              <AdminRequestForm user={user} />
            </div>
          </div>
        )}
      </div>

      <div className="bg-white py-6 rounded-lg w-full">
        <div className="flex justify-end items-end">
          <FormControl
            margin="normal"
            className="w-full lg:w-1/4 sm:w-1/2 md:w-1/2 bg-gray-100 ml-auto"
          >
            <InputLabel>Filter by Office</InputLabel>
            <Select
              value={selectedOffice}
              onChange={(e) => setSelectedOffice(e.target.value)}
              label="Filter by Office"
            >
              <MenuItem value="">All Offices</MenuItem>
              {offices.map((officeName) => (
                <MenuItem key={officeName} value={officeName}>
                  {officeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="300px"
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="300px"
          >
            <p>{error}</p>
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} className="hidden lg:block">
              <Table>
                <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Office</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Action</TableCell> {/* Updated to Action */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentRequests.length > 0 ? (
                    currentRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>{request.title}</TableCell>
                        <TableCell>{request.description}</TableCell>
                        <TableCell>{request.office}</TableCell>
                        <TableCell>{request.type}</TableCell>
                        <TableCell>
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenPreview(request)}
                          >
                            <Visibility />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No requests available.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Grid View for Mobile */}
            <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              {currentRequests.length > 0 ? (
                currentRequests.map((request) => (
                  <div
                    key={request.id}
                    className="border p-4 rounded-lg shadow-md"
                  >
                    <h3 className="font-semibold">{request.title}</h3>
                    <p className="text-sm text-gray-500">
                      {request.description}
                    </p>
                    <p className="text-sm text-gray-500">
                      Office: {request.office}
                    </p>
                    <p className="text-sm text-gray-500">
                      Type: {request.type}
                    </p>
                    <div className="flex justify-end items-end">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenPreview(request)}
                      >
                        <Visibility />
                      </IconButton>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-2 text-center">
                  No requests available.
                </div>
              )}
            </div>
            {/* Pagination Component */}
            <CustomPagination
              currentPage={currentPage}
              totalItems={filteredRequests.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
            {/* Modal for Request Preview */}
            <Dialog
              open={openPreview}
              onClose={handleClosePreview}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle className="flex justify-between items-center border-b-2">
                <span> {previewRequest?.title}</span>
                <button
                  onClick={handleClosePreview}
                  className="text-gray-500 hover:text-black"
                >
                  &#10005;
                </button>
              </DialogTitle>
              <DialogContent className="px-6">
                {previewRequest && (
                  <div>
                    <p>
                      <strong>Description:</strong>{" "}
                      {showFullDescription
                        ? previewRequest.description
                        : previewRequest.description.length > MAX_LENGTH
                        ? previewRequest.description.substring(0, MAX_LENGTH) +
                          "..."
                        : previewRequest.description}
                      {previewRequest.description.length > MAX_LENGTH && (
                        <button
                          onClick={toggleDescription}
                          className="text-blue-500 ml-2"
                        >
                          {showFullDescription ? "Show Less" : "More..."}
                        </button>
                      )}
                    </p>
                    <p>
                      <strong>Office:</strong> {previewRequest.office}
                    </p>
                    <p>
                      <strong>Type:</strong> {previewRequest.type}
                    </p>
                    <p>
                      <strong>User:</strong> {getUser}
                    </p>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
};

export default Supports;
